import {AlertCircle, Coffee, DollarSign, Loader2, Utensils} from "lucide-react";
import {useEffect, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";

import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert";
import {Button} from "@/components/ui/button";
import {cn} from "@/lib/utils";
import {useToast} from "@/src/modules/core/hooks";
import {formatTime} from "@/src/modules/core/lib";
import {captureParkingSubmitError} from "@/src/modules/core/lib/glitchtip/parking-capture";
import {resolveShiftGpsForSubmit} from "@/src/modules/core/lib/resolve-shift-gps-coordinates";
import {reconcileCompliance} from "@/src/modules/core/lib/reconcile-compliance";
import {useCoordinatesGate} from "@/src/modules/core/hooks/use-coordinates-gate";
import {CoordinatesRequiredGate} from "@/src/modules/core/components";
import {shiftsService} from "@/src/modules/shifts/services";
import {Shift, ShiftBreak} from "@/src/modules/shifts/types";

import {useBreakAction} from "../../hooks";
import {
  getBreakActionErrorTitle,
  isBreakBusinessError,
} from "../../lib/break-action-errors";
import {getShiftBreaks} from "@/src/modules/shifts/lib/shift-breaks";
import {BreakAction, BreakActionRequest} from "../../types";

interface BreakCardProps {
  shift: Shift;
}

export function BreakCard({shift}: BreakCardProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [shiftBreak, setShiftBreak] = useState<ShiftBreak | null>(null);
  const {isPending: isBreakStarting, mutateAsync: breakAction} = useBreakAction();
  const {submitBlockedByLocation} = useCoordinatesGate(true);
  const isRoleFlexibleBreak = shiftsService.getIsRoleFlexibleBreak();
  const hasRestBreak = false;
  const hasMealBreak = false;
  const wasMealBreakInterrupted = false;
  const wasRestBreakInterrupted = false;

  const handleBreakAction = async (breakItem: ShiftBreak, action: BreakAction) => {
    try {
      const gps = await resolveShiftGpsForSubmit(shift, "break_action");
      if (!gps) {
        return;
      }
      const data = {
        shift_break_id: breakItem.shift_break_id,
        gps_coordinates: gps.gps_coordinates,
        gps_source: gps.gps_source,
        action,
      } as BreakActionRequest;

      await breakAction(data);
      toast.success(
        `Your ${breakItem.break_type === "lunch" ? "meal" : "rest"} break has started`,
      );

      await reconcileCompliance(queryClient, {shiftBreakId: breakItem.shift_break_id});
    } catch (e) {
      if (isBreakBusinessError(e)) {
        toast.error(getBreakActionErrorTitle(e));
      } else {
        captureParkingSubmitError(e, {feature: "shifts", operation: "break_action"});
      }
    } finally {
      setShiftBreak(null);
    }
  };

  useEffect(() => {
    if (shiftBreak) {
      void handleBreakAction(shiftBreak, BreakAction.START);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- start break once when user selects a slot
  }, [shiftBreak]);

  const shiftBreaks = getShiftBreaks(shift);
  const scheduledBreaks = shiftBreaks.filter((b) => b.status === "scheduled");
  const hasScheduledBreaks = scheduledBreaks.length > 0;

  if (shiftBreaks.length === 0) {
    return null;
  }

  return (
    <Alert className="border-primary/50 bg-primary/5">
      <Coffee className="size-4 text-primary" />
      <AlertTitle>{hasScheduledBreaks ? "Available Breaks" : "Breaks"}</AlertTitle>
      <AlertDescription>
        {submitBlockedByLocation ? <CoordinatesRequiredGate className="mb-3" /> : null}
        {isRoleFlexibleBreak && hasScheduledBreaks ? (
          <p className="text-sm">
            You can start a break by clicking on the button below at any time during the shift.
          </p>
        ) : null}
        <div className="flex gap-2 flex-wrap">
          {shiftBreaks.map((sb) => (
            <Button
              key={`break-${sb.break_type}-${sb.shift_break_id}`}
              variant="outline"
              disabled={
                sb.status !== "scheduled" ||
                !isRoleFlexibleBreak ||
                submitBlockedByLocation ||
                isBreakStarting
              }
              className={cn(
                "block h-fit border border-primary/50 rounded-md p-2 bg-white",
                sb.status === "completed" && "bg-green-500/10 border-green-500/20 text-green-700",
              )}
              onClick={() => {
                if (sb.status === "scheduled") {
                  setShiftBreak(sb);
                }
              }}
            >
              <div className="flex items-start justify-center gap-1 font-semibold">
                <div className="flex flex-col items-start justify-center">
                  {sb.break_type === "rest" ? (
                    <Coffee className="size-4" />
                  ) : (
                    <Utensils className="size-4" />
                  )}
                  {sb.is_paid === 0 ? <DollarSign className="size-4" /> : null}
                </div>
                <div className="flex flex-col items-start">
                  {sb.break_type === "lunch" ? "Meal break" : "Rest"}
                  {isBreakStarting &&
                  shiftBreak &&
                  sb.shift_break_id === shiftBreak.shift_break_id ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : sb.status === "completed" ? (
                    <small>Completed</small>
                  ) : sb.status === "active" ? (
                    <small>In progress</small>
                  ) : (
                    <small>at {formatTime(sb.execution_start_datetime ?? "")}</small>
                  )}
                </div>
              </div>
            </Button>
          ))}
          {wasRestBreakInterrupted && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {}}
              className="gap-2 border-destructive/50 text-destructive hover:bg-destructive/10"
            >
              <Coffee className="size-4" />
              Restart Rest Break
            </Button>
          )}
          {wasMealBreakInterrupted && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {}}
              className="gap-2 border-destructive/50 text-destructive hover:bg-destructive/10"
            >
              <Utensils className="size-4" />
              Restart Meal Break
            </Button>
          )}
          {hasRestBreak && !wasRestBreakInterrupted && (
            <Button size="sm" variant="outline" onClick={() => {}} className="gap-2">
              <Coffee className="size-4" />
              Start Rest Break
            </Button>
          )}
          {hasMealBreak && !wasMealBreakInterrupted && (
            <Button size="sm" variant="outline" onClick={() => {}} className="gap-2">
              <Utensils className="size-4" />
              Start Meal Break
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}
